import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Trash2, 
  User,
  Mail,
  Phone,
  Building2,
  Briefcase,
  Calendar,
  TrendingUp,
  PhoneCall,
  Send,
  Eye,
  MousePointer,
  Repeat,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { useToast } from '@/components/common/Toast';
import { PageHeader } from '@/components/common/PageHeader';
import { Pagination } from '@/components/common/Pagination';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/common/Dialog';
import * as contactsService from '@/services/api/contactsService';
import * as accountsService from '@/services/api/accountsService';
import type { Contact, TimelineEvent, TimelineResponse } from '@/services/api/contactsService';
import type { Account } from '@/services/api/accountsService';
import SendMagnetModal from '@/features/communication/SendMagnetModal';

// Premium color mappings
const tierColors = {
  1: 'tier1',     // Gold gradient
  2: 'tier2',     // Purple gradient
  3: 'tier3',     // Gray gradient
} as const;

const accountStatusColors = {
  ACTIVE: 'active',           // Emerald gradient
  INACTIVE: 'inactive',       // Muted gray
  MQL_READY: 'mqlReady',     // Pink gradient with pulse
  MQL_SUBMITTED: 'mqlSubmitted', // Blue gradient
} as const;

const eventIcons = {
  CALL: PhoneCall,
  MAGNET_DELIVERY: Send,
  ENGAGEMENT: Eye,
};

const eventSubTypeIcons = {
  OPEN: Eye,
  CLICK: MousePointer,
  REVISIT: Repeat,
};

interface ExpandedMetadata {
  [key: string]: boolean;
}

export default function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const [contact, setContact] = useState<Contact | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [timeline, setTimeline] = useState<TimelineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedMetadata, setExpandedMetadata] = useState<ExpandedMetadata>({});
  const [sendMagnetOpen, setSendMagnetOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch contact details
        const contactData = await contactsService.getContact(id);
        setContact(contactData);
        
        // Set account from contact data (backend includes it)
        if (contactData.account) {
          setAccount(contactData.account as any);
        }
        
        // Fetch timeline
        const timelineData = await contactsService.getContactTimeline(id, {
          page: currentPage,
          limit: 20,
        });
        setTimeline(timelineData);
        
      } catch (error: any) {
        console.error('Error fetching contact:', error);
        addToast({
          type: 'error',
          title: 'Error',
          description: error.response?.data?.error || 'Failed to load contact details',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, currentPage, refreshKey]);

  const handleDelete = async () => {
    if (!contact) return;

    try {
      await contactsService.deleteContact(contact.id);
      
      addToast({
        type: 'success',
        title: 'Contact Deleted',
        description: `${contact.firstName || contact.first_name} ${contact.lastName || contact.last_name} has been deleted successfully.`,
      });
      
      navigate('/contacts');
    } catch (error: any) {
      addToast({
        type: 'error',
        title: 'Error',
        description: error.response?.data?.error || 'Failed to delete contact',
      });
    }
  };

  const toggleMetadata = (eventId: string) => {
    setExpandedMetadata(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getEventIcon = (event: TimelineEvent) => {
    if (event.type === 'ENGAGEMENT' && event.subType) {
      const IconComponent = eventSubTypeIcons[event.subType as keyof typeof eventSubTypeIcons];
      return IconComponent || Eye;
    }
    return eventIcons[event.type as keyof typeof eventIcons] || User;
  };

  const getEventColor = (color: string) => {
    const colorMap: Record<string, string> = {
      green: 'text-green-600 bg-green-50',
      blue: 'text-blue-600 bg-blue-50',
      purple: 'text-purple-600 bg-purple-50',
      orange: 'text-orange-600 bg-orange-50',
    };
    return colorMap[color] || 'text-gray-600 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Loading contact details...</p>
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="text-center py-12">
        <User className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-2xl font-bold mb-2">Contact Not Found</h2>
        <p className="text-muted-foreground mb-4">
          The contact you're looking for doesn't exist or has been deleted.
        </p>
        <Button onClick={() => navigate('/contacts')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Contacts
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        onBack={() => navigate('/contacts')}
        title={`${contact.firstName || contact.first_name} ${contact.lastName || contact.last_name}`}
        description={contact.title || undefined}
        actions={
          <>
            <Button onClick={() => setSendMagnetOpen(true)}>
              <Send className="h-4 w-4 mr-2" />
              Send Magnet
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/contacts/${contact.id}/edit`)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Contact
            </Button>
            <Button
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </>
        }
      />
      {(contact.isDecisionMaker || contact.is_decision_maker || /vp |cto|coo|ceo|cfo|director|chief|president/i.test(contact.title || "")) && (
        <div className="-mt-2"><Badge variant="default">Decision Maker</Badge></div>
      )}

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Sidebar - Contact Information */}
        <div className="lg:col-span-1 space-y-6">
          {/* Contact Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {contact.email && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </label>
                  <a 
                    href={`mailto:${contact.email}`}
                    className="text-base text-primary hover:underline mt-1 block"
                  >
                    {contact.email}
                  </a>
                </div>
              )}
              
              {contact.phone && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Phone
                  </label>
                  <a 
                    href={`tel:${contact.phone}`}
                    className="text-base text-primary hover:underline mt-1 block"
                  >
                    {contact.phone}
                  </a>
                </div>
              )}
              
              {contact.title && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    Title
                  </label>
                  <p className="text-base mt-1">{contact.title}</p>
                </div>
              )}
              
              {contact.department && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Department</label>
                  <p className="text-base mt-1">{contact.department}</p>
                </div>
              )}
              
              {(contact.linkedinUrl || contact.linkedin_url) && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">LinkedIn</label>
                  <a 
                    href={(contact.linkedinUrl || contact.linkedin_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base text-primary hover:underline mt-1 block"
                  >
                    View Profile
                  </a>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Created
                </label>
                <p className="text-base mt-1">
                  {contact.created_at && !isNaN(new Date(contact.created_at).getTime()) ? new Date(contact.created_at).toLocaleDateString() : contact.createdAt && !isNaN(new Date(contact.createdAt).getTime()) ? new Date(contact.createdAt).toLocaleDateString() : '—'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Account Association Card */}
          {account && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Account
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  className="cursor-pointer hover:bg-muted/50 p-3 rounded-lg transition-colors"
                  onClick={() => navigate(`/accounts/${account.id}`)}
                >
                  <p className="font-medium">{account.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {account.industryVertical || 'No industry specified'}
                    {account.region && ` • ${account.region}`}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    {account.abmTier && (
                      <Badge variant={tierColors[account.abmTier as keyof typeof tierColors] || 'default'}>
                        Tier {account.abmTier}
                      </Badge>
                    )}
                    <Badge variant={accountStatusColors[account.status as keyof typeof accountStatusColors] || 'default'}>
                      {account.status.replace('_', ' ')}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Engagement Summary */}
          {timeline && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Engagement Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Total Calls</span>
                  <span className="font-bold">{timeline.summary.totalCalls}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Content Delivered</span>
                  <span className="font-bold">{timeline.summary.totalDeliveries}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Engagements</span>
                  <span className="font-bold">{timeline.summary.totalEngagements}</span>
                </div>
                <div className="flex items-center justify-between pt-3 border-t">
                  <span className="text-sm font-medium">Total Events</span>
                  <span className="font-bold text-primary">{timeline.summary.totalEvents}</span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Side - Timeline */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Interaction Timeline</CardTitle>
              <p className="text-sm text-muted-foreground">
                Complete history of all interactions with {contact.firstName || contact.first_name}
              </p>
            </CardHeader>
            <CardContent>
              {!timeline || timeline.data.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No interaction history yet</p>
                  <p className="text-sm mt-1">Interactions will appear here as they occur</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Timeline Events */}
                  <div className="relative space-y-6">
                    {/* Vertical Line */}
                    <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>
                    
                    {timeline.data.map((event) => {
                      const EventIcon = getEventIcon(event);
                      const { date, time } = formatTimestamp(event.timestamp);
                      const isExpanded = expandedMetadata[event.id];
                      
                      return (
                        <div key={event.id} className="relative pl-16">
                          {/* Icon */}
                          <div className={`absolute left-0 w-12 h-12 rounded-full flex items-center justify-center ${getEventColor(event.color)}`}>
                            <EventIcon className="h-5 w-5" />
                          </div>
                          
                          {/* Content */}
                          <div className="bg-muted/30 rounded-lg p-4 hover:bg-muted/50 transition-colors">
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h4 className="font-medium">{event.title}</h4>
                                <p className="text-sm text-muted-foreground">{event.description}</p>
                              </div>
                              <div className="text-right text-sm text-muted-foreground">
                                <div>{date}</div>
                                <div>{time}</div>
                              </div>
                            </div>
                            
                            {/* Metadata Toggle */}
                            {event.metadata && Object.keys(event.metadata).length > 0 && (
                              <div className="mt-3">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleMetadata(event.id)}
                                  className="text-xs"
                                >
                                  {isExpanded ? (
                                    <>
                                      <ChevronUp className="h-3 w-3 mr-1" />
                                      Hide Details
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="h-3 w-3 mr-1" />
                                      Show Details
                                    </>
                                  )}
                                </Button>
                                
                                {isExpanded && (
                                  <div className="mt-2 p-3 bg-background rounded border text-sm space-y-1">
                                    {Object.entries(event.metadata).map(([key, value]) => (
                                      <div key={key} className="flex gap-2">
                                        <span className="font-medium text-muted-foreground capitalize">
                                          {key.replace(/([A-Z])/g, ' $1').trim()}:
                                        </span>
                                        <span className="text-foreground">
                                          {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {timeline.pagination.totalPages > 1 && (
                    <div className="mt-6">
                      <Pagination
                        currentPage={timeline.pagination.page}
                        totalPages={timeline.pagination.totalPages}
                        pageSize={timeline.pagination.limit}
                        totalItems={timeline.pagination.total}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={() => {}}
                      />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Send Magnet Modal */}
      <SendMagnetModal
        open={sendMagnetOpen}
        onClose={() => setSendMagnetOpen(false)}
        contactId={contact.id}
        contactName={`${contact.firstName || contact.first_name} ${contact.lastName || contact.last_name}`}
        contactEmail={contact.email}
        currentStage={contact.currentStage}
        onSent={() => setRefreshKey((k) => k + 1)}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogClose onClose={() => setDeleteDialogOpen(false)} />
          <DialogHeader>
            <DialogTitle>Delete Contact</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              <span className="font-semibold">{contact.firstName || contact.first_name} {contact.lastName || contact.last_name}</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
